import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { 
  AppBar, Divider, Toolbar, Typography, Button, Grid, Container, Box, Badge, Paper, Tooltip,
  ToggleButtonGroup, ToggleButton, Fab, CircularProgress, ThemeProvider, createTheme, CssBaseline
} from '@mui/material';
import { 
  FilterList, Brightness4, Brightness7, ContactMail, KeyboardArrowUp as KeyboardArrowUpIcon,
  CheckBox as SelectAllIcon, CheckBoxOutlineBlank as DeselectAllIcon
} from '@mui/icons-material';
import { useAuth, useClerk, SignIn } from '@clerk/clerk-react';

import GameCard from './GameCard';
import GameDetailsDialog from './GameDetailsDialog';
import FilterDrawer from './FilterDrawer';

const getTheme = (mode) => createTheme({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          primary: { main: '#1976d2' },
          secondary: { main: '#dc004e' },
        }
      : {
          primary: { main: '#90caf9' },
          secondary: { main: '#f4364c' },
        }),
  }
});

const SteamGameBrowser = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const { signOut } = useClerk();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({ 
    tags: [], 
    excludeTags: ['NSFW', 'Nudity', 'Sexual Content'],
    minScore: 0, 
    maxScore: 100, 
    showHidden: false 
  });
  const [darkMode, setDarkMode] = useState(true);
  const [selectedGames, setSelectedGames] = useState([]);
  const [detailsGame, setDetailsGame] = useState(null);
  const [page, setPage] = useState(1);
  const [watchlist, setWatchlist] = useState([]);
  const [currentView, setCurrentView] = useState('games');
  const [showJumpToTop, setShowJumpToTop] = useState(false);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [cooldown, setCooldown] = useState(false);

  const topRef = useRef(null);
  const secondaryNavRef = useRef(null);

  const theme = useMemo(() => getTheme(darkMode ? 'dark' : 'light'), [darkMode]);

  const filteredGames = useMemo(() => {
    const gamesList = currentView === 'games' 
      ? games.filter(game => !watchlist.includes(game.appId)) 
      : games.filter(game => watchlist.includes(game.appId));

    return gamesList
      .filter(game =>
        game.confidenceRating >= filters.minScore &&
        game.confidenceRating <= filters.maxScore &&
        (filters.tags.length === 0 || filters.tags.some(tag => game.tags.includes(tag))) &&
        (filters.excludeTags.length === 0 || !filters.excludeTags.some(tag => game.tags.includes(tag))) &&
        (filters.showHidden || !game.hidden)
      );
  }, [games, watchlist, filters, currentView]);

  const fetchGames = useCallback(async (token = null) => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL('https://game-scouter.indie.io/api/v1/steam/potential-games');
      url.searchParams.append('limit', '20');
      if (token) {
        url.searchParams.append('next', token);
      }
      console.log('Fetching games from URL:', url.toString());
      
      const response = await fetch(url);
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Received data:', data);
      
      setGames(prevGames => token ? [...prevGames, ...data.data] : data.data);
      setNextToken(data.next);
    } catch (error) {
      console.error('Error fetching games:', error);
      setError('Failed to fetch games. Please try again later.');
    }
    setLoading(false);
  }, []);

  const observer = useRef();
  const cooldownTimer = useRef(null);

  const lastGameElementRef = useCallback(node => {
    if (loading || !hasMore || currentView !== 'games' || cooldown) return;
    if (currentView !== 'games') return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && nextToken) {
        console.log('Fetching more games...');
        fetchGames(nextToken);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore, currentView, cooldown, fetchGames]);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  useEffect(() => {
    const handleScroll = () => {
      setShowJumpToTop(window.scrollY > 300);
      if (secondaryNavRef.current) {
        if (window.scrollY > secondaryNavRef.current.offsetTop) {
          secondaryNavRef.current.style.position = 'fixed';
          secondaryNavRef.current.style.top = '0';
          secondaryNavRef.current.style.left = '0';
          secondaryNavRef.current.style.right = '0';
          secondaryNavRef.current.style.zIndex = '1000';
        } else {
          secondaryNavRef.current.style.position = 'static';
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGameSelect = (gameId) => {
    setSelectedGames(prev => 
      prev.includes(gameId) ? prev.filter(id => id !== gameId) : [...prev, gameId]
    );
  };

  const handleSelectAll = () => {
    if (selectedGames.length === 0) {
      setSelectedGames(filteredGames.map(game => game.appId));
    } else {
      setSelectedGames([]);
    }
  };

  const handleMoreDetails = (game) => {
    setDetailsGame(game);
  };

  const handleCloseDetails = () => {
    setDetailsGame(null);
  };

  const handleContact = () => {
    console.log("Contact button clicked. Selected games:", selectedGames);
    // Implement your contact functionality here
  };

  const handleToggleVisibility = (gameId) => {
    setGames(prevGames => prevGames.map(game => 
      game.appId === gameId ? { ...game, hidden: !game.hidden } : game
    ));
  };

  const handleToggleWatchlist = (gameId) => {
    setWatchlist(prev => 
      prev.includes(gameId) ? prev.filter(id => id !== gameId) : [...prev, gameId]
    );
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setCurrentView(newView);
    }
  };

  const handleJumpToTop = () => {
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      console.log("User signed out successfully");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div ref={topRef}>
        <AppBar position="static">
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <img 
                  src="/logo.png" 
                  alt="indie.io" 
                  style={{ height: '40px', marginRight: '16px' }} 
                />
                <Divider orientation="vertical" style={{ height: '40px', marginRight: '16px' }} sx={{ display: { xs: 'none', sm: 'block' } }}/>
                <Typography flexItem variant="h6" component="div" sx={{ display: { xs: 'none', sm: 'block' } }}>
                  Game Scouter Preview
                </Typography>
              </Box>
              {isSignedIn && (
                <Badge badgeContent={selectedGames.length} color="secondary">
                  <Button 
                    color="inherit" 
                    startIcon={<ContactMail />} 
                    onClick={handleContact}
                    disabled={selectedGames.length === 0}
                  >
                    Contact
                  </Button>
                </Badge>
              )}
            </Toolbar>
          </Container>
        </AppBar>

        {isSignedIn ? (
          <>
            <Paper elevation={1} ref={secondaryNavRef}>
              <Container maxWidth="lg">
                <Box sx={{ py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <ToggleButtonGroup
                    value={currentView}
                    exclusive
                    onChange={handleViewChange}
                    aria-label="view selector"
                    size="small"
                  >
                    <ToggleButton value="games" aria-label="all games">
                      All Games
                    </ToggleButton>
                    <ToggleButton value="watchlist" aria-label="watch list">
                      Watch List
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={selectedGames.length === 0 ? "Select All" : "Deselect All"}>
                      <Button
                        color="primary"
                        startIcon={selectedGames.length === 0 ? <SelectAllIcon /> : <DeselectAllIcon />}
                        onClick={handleSelectAll}
                        disabled={filteredGames.length === 0}
                        size="small"
                      >
                        {selectedGames.length === 0 ? "Select All" : "Deselect All"}
                      </Button>
                    </Tooltip>
                    <Tooltip title="Filters">
                      <Button
                        color="primary"
                        startIcon={<FilterList />}
                        onClick={() => setIsFilterOpen(true)}
                        sx={{ ml: 1 }}
                        size="small"
                      >
                        Filters
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              </Container>
            </Paper>
            <Container maxWidth="lg" sx={{ py: 4 }}>
              <Grid container spacing={3}>
                {filteredGames.map((game, index) => (
                  <Grid 
                    item 
                    key={game.appId} 
                    xs={12} 
                    sm={6} 
                    md={4} 
                    ref={index === filteredGames.length - 1 ? lastGameElementRef : null}
                  >
                    <GameCard 
                      game={{
                        ...game,
                        inWatchlist: watchlist.includes(game.appId)
                      }}
                      selected={selectedGames.includes(game.appId)}
                      onSelect={() => handleGameSelect(game.appId)}
                      onMoreDetails={() => handleMoreDetails(game)}
                      onToggleVisibility={() => handleToggleVisibility(game.appId)}
                      onToggleWatchlist={() => handleToggleWatchlist(game.appId)}
                    />
                  </Grid>
                ))}
              </Grid>
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {error && (
                <Typography sx={{ mt: 4, textAlign: 'center' }} color="error">
                  {error}
                </Typography>
              )}
              {filteredGames.length === 0 && !loading && (
                <Typography sx={{ mt: 4, textAlign: 'center' }}>
                  No games found matching your filters.
                </Typography>
              )}
            </Container>
<FilterDrawer
  open={isFilterOpen}
  onClose={() => setIsFilterOpen(false)}
  filters={filters}
  setFilters={setFilters}
  allTags={Array.from(new Set(games.flatMap(game => game.tags)))}
/>
            <GameDetailsDialog 
              open={Boolean(detailsGame)} 
              onClose={handleCloseDetails}
              game={detailsGame}
            />
          </>
        ) : (
          <Box 
            sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minHeight: '100vh',
              width: '100vw',
              pt: 8
            }}
          >
            <SignIn
              appearance={{
                elements: {
                  footer: 'hidden',
                  footerActionLink: 'hidden'
                }
              }}
            />
          </Box>
        )}
        <Box sx={{ position: 'fixed', bottom: 16, right: 16, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {showJumpToTop && (
            <Fab
              color="primary"
              size="small"
              onClick={handleJumpToTop}
              aria-label="jump to top"
            >
              <KeyboardArrowUpIcon />
            </Fab>
          )}
          <Fab
            color="primary"
            size="small"
            onClick={() => setDarkMode(!darkMode)}
            aria-label="toggle dark mode"
          >
            {darkMode ? <Brightness7 /> : <Brightness4 />}
          </Fab>
        </Box>
      </div>
    </ThemeProvider>
  );
};

export default SteamGameBrowser;